import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ScanLine, FolderTree, Sparkles, ArrowRight, FileText, Zap } from "lucide-react";
import { Button } from "@/components/ui/button";
import InstallPWAButton from "@/components/app/InstallPWAButton";

export default function Landing() {
  return (
    <div className="min-h-screen bg-[#F8F8F6] text-[#0A0A0A]">
      {/* Nav */}
      <header className="sticky top-0 z-50 border-b border-black/5 backdrop-blur-xl bg-white/70">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-[#0A0A0A] flex items-center justify-center rounded-xl">
              <ScanLine className="w-4 h-4 text-white" />
            </div>
            <span className="font-display font-bold text-lg tracking-tighter">Archivio Smart</span>
          </div>
          <div className="flex items-center gap-3">
            <InstallPWAButton />
            <Link to="/login" data-testid="nav-login-link" className="text-sm hover:text-[#0A0A0A] transition-colors">Accedi</Link>
            <Link to="/register" data-testid="nav-register-link">
              <Button data-testid="nav-register-btn" className="rounded-xl bg-[#0A0A0A] hover:bg-[#1A1A1A] text-white h-9 px-4">
                Inizia gratis
              </Button>
            </Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden border-b border-black/5">
        <div className="absolute inset-0 grid-bg opacity-60 pointer-events-none" />
        <div className="max-w-7xl mx-auto px-6 py-24 lg:py-32 relative">
          <div className="grid lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-7">
              <motion.div
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
              >
                <div className="overline text-[#0A0A0A] mb-6">— OCR + AI PER I TUOI DOCUMENTI</div>
                <h1 className="font-display font-black tracking-tighter text-5xl sm:text-6xl lg:text-7xl leading-[0.95] mb-6">
                  Scansiona.<br />
                  Estrai. <span className="text-[#0A0A0A]">Archivia.</span>
                </h1>
                <p className="text-lg text-[#5C5C5C] max-w-xl mb-10 leading-relaxed">
                  Scatta una foto a scontrini, ricevute e fatture. L'intelligenza artificiale legge il contenuto,
                  rinomina il file e lo archivia nella cartella giusta. Zero fatica, tutto ordinato.
                </p>
                <div className="flex flex-wrap gap-3">
                  <Link to="/register">
                    <Button data-testid="hero-cta-primary" className="rounded-xl bg-[#0A0A0A] hover:bg-[#1A1A1A] text-white h-12 px-6 text-sm">
                      Prova ora gratis <ArrowRight className="ml-2 w-4 h-4" />
                    </Button>
                  </Link>
                  <Link to="/login">
                    <Button data-testid="hero-cta-secondary" variant="outline" className="rounded-xl h-12 px-6 text-sm border-black/20 hover:bg-black/5">
                      Ho già un account
                    </Button>
                  </Link>
                </div>
              </motion.div>
            </div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.15 }}
              className="lg:col-span-5"
            >
              <div className="relative border border-black/10 bg-white rounded-xl p-6 shadow-sm">
                <div className="overline mb-3 text-[#8A8A8A]">DOCUMENTO IN LAVORAZIONE</div>
                <div className="relative border border-dashed border-black/15 rounded-xl h-64 flex items-center justify-center bg-[#FAFAFA] overflow-hidden">
                  <div className="scan-beam" />
                  <FileText className="w-16 h-16 text-black/20" />
                </div>
                <div className="mt-4 space-y-2 font-mono-plex text-xs">
                  <div className="flex justify-between border-b border-black/5 pb-2"><span className="text-[#8A8A8A]">TIPO</span><span>Scontrino</span></div>
                  <div className="flex justify-between border-b border-black/5 pb-2"><span className="text-[#8A8A8A]">ESERCENTE</span><span>Esselunga</span></div>
                  <div className="flex justify-between border-b border-black/5 pb-2"><span className="text-[#8A8A8A]">DATA</span><span>15.03.2025</span></div>
                  <div className="flex justify-between"><span className="text-[#8A8A8A]">TOTALE</span><span className="font-bold">€ 47,82</span></div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="max-w-7xl mx-auto px-6 py-24">
        <div className="overline text-[#0A0A0A] mb-4">— COME FUNZIONA</div>
        <h2 className="font-display font-bold text-4xl lg:text-5xl tracking-tighter mb-16 max-w-2xl">
          Tre passi. Zero manualità.
        </h2>
        <div className="grid md:grid-cols-3 gap-px bg-black/10 border border-black/10 rounded-xl overflow-hidden">
          {[
            { icon: ScanLine, num: "01", title: "Carica o scatta", desc: "Foto, PDF o scansione. Ogni formato accettato." },
            { icon: Sparkles, num: "02", title: "AI legge tutto", desc: "GPT-5.2 estrae dati e riconosce se è utile per ISEE o 730/CU." },
            { icon: FolderTree, num: "03", title: "Archivio pulito", desc: "File rinominato e messo nella cartella giusta. Pronto per il commercialista." },
          ].map((f) => (
            <div key={f.num} className="bg-white p-8 hover:bg-[#F8F8F6] transition-colors">
              <div className="font-mono-plex text-xs text-[#8A8A8A] mb-6">{f.num}</div>
              <f.icon className="w-8 h-8 text-[#0A0A0A] mb-6" strokeWidth={1.5} />
              <h3 className="font-display font-bold text-2xl mb-3 tracking-tight">{f.title}</h3>
              <p className="text-sm text-[#5C5C5C] leading-relaxed">{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="border-t border-black/5 bg-[#0A0A0A] text-white">
        <div className="max-w-7xl mx-auto px-6 py-20 flex flex-col md:flex-row items-start md:items-end justify-between gap-8">
          <div>
            <div className="overline text-white/50 mb-4">— PRONTI?</div>
            <h2 className="font-display font-bold text-4xl lg:text-5xl tracking-tighter max-w-xl">
              Metti in ordine il tuo archivio.<br />Comincia adesso.
            </h2>
          </div>
          <Link to="/register">
            <Button data-testid="footer-cta-btn" className="rounded-xl bg-white text-[#0A0A0A] hover:bg-white/90 h-12 px-6">
              Crea account <Zap className="ml-2 w-4 h-4" />
            </Button>
          </Link>
        </div>
        <div className="border-t border-white/10">
          <div className="max-w-7xl mx-auto px-6 py-6 text-xs text-white/50 flex justify-between">
            <span>© 2026 Archivio Smart</span>
            <span className="font-mono-plex">v.1.0</span>
          </div>
        </div>
      </section>
    </div>
  );
}
